import { useRef, useState, useEffect } from "react";
import { CardBack } from "./CardBack";
import { TreatmentCard } from "./TreatmentCard";
import type { TreatmentCard as TreatmentCardType } from "../types/game";

interface Submission {
  playerId: string;
  playerName: string;
  cards: TreatmentCardType[] | "submitted";
  isRevealed: boolean;
}

interface TreatmentSubmissionsProps {
  submissions: Submission[];
  onCardClick?: (playerId: string) => void;
  selectable?: boolean;
  className?: string;
}

const GAP_SIZE = 12; // Gap between cards in pixels
const MIN_CARD_WIDTH = 80; // Minimum to fit symbols properly
const MAX_CARD_WIDTH = 160; // Maximum card width
const CARD_ASPECT_RATIO = 1.5; // Height = width * 1.5

function calculateCardSize(
  containerWidth: number,
  cardCount: number,
): { width: number; height: number } {
  if (cardCount === 0) return { width: 0, height: 0 };

  const totalGaps = (cardCount - 1) * GAP_SIZE;
  const availableWidth = containerWidth - totalGaps;
  const cardWidth = Math.floor(availableWidth / cardCount);

  // Clamp to bounds
  const clampedWidth = Math.max(
    MIN_CARD_WIDTH,
    Math.min(cardWidth, MAX_CARD_WIDTH),
  );
  const cardHeight = Math.floor(clampedWidth * CARD_ASPECT_RATIO);

  return { width: clampedWidth, height: cardHeight };
}

export function TreatmentSubmissions({
  submissions,
  onCardClick,
  selectable = false,
  className = "",
}: TreatmentSubmissionsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cardSize, setCardSize] = useState({ width: 80, height: 120 });

  // Update card size when container resizes
  useEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      if (containerRef.current && submissions.length > 0) {
        const containerWidth = containerRef.current.offsetWidth;
        const newSize = calculateCardSize(containerWidth, submissions.length);
        setCardSize(newSize);
      }
    };

    // Initial size calculation
    updateSize();

    // Watch for resize
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [submissions.length]);

  if (submissions.length === 0) {
    return (
      <div className={`text-center text-gray-400 py-4 ${className}`}>
        No treatments submitted yet
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`flex justify-center items-end gap-2 ${className}`}
      style={{ gap: `${GAP_SIZE}px` }}
    >
      {submissions.map((submission) => {
        const isRevealed =
          submission.isRevealed && Array.isArray(submission.cards);

        if (
          isRevealed &&
          Array.isArray(submission.cards) &&
          submission.cards.length > 0
        ) {
          // Show the first treatment card (revealed)
          return (
            <div
              key={submission.playerId}
              className={selectable ? "cursor-pointer" : ""}
              onClick={
                selectable
                  ? () => onCardClick?.(submission.playerId)
                  : undefined
              }
              onKeyDown={
                selectable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onCardClick?.(submission.playerId);
                      }
                    }
                  : undefined
              }
              role={selectable ? "button" : undefined}
              tabIndex={selectable ? 0 : undefined}
            >
              <TreatmentCard
                card={submission.cards[0]}
                width={cardSize.width}
                height={cardSize.height}
                selectable={selectable}
              />
              {submission.cards.length > 1 && (
                <div className="text-xs text-center text-gray-400 mt-1">
                  +{submission.cards.length - 1} more
                </div>
              )}
            </div>
          );
        }

        // Show card back (not revealed)
        return (
          <CardBack
            key={submission.playerId}
            width={cardSize.width}
            height={cardSize.height}
            playerName={submission.playerName}
          />
        );
      })}
    </div>
  );
}
